import type {GetStaticPaths, GetStaticProps} from 'next'
import {useRouter} from 'next/router'
import React, {useEffect, useState} from 'react'
import {Datalayer} from '@mirohq-internal/website-analytics'
import {sessionStorage} from '@mirohq-internal/website-utils'
import getLabels from '../../src/api/getLabels'
import getQuestions from '../../src/api/getQuestions'
import useUserDevice from '../../src/components/account-is-set/useDevice'
import {ANALYTICS_QUESTIONNAIRE_STORAGE_KEY} from '../../src/components/constants'
import {RETURN_URL_STORAGE_KEY} from '../../src/components/constants'
import Questionnaire from '../../src/components/Questionnaire'
import useAnalytics from '../../src/hooks/useAnalytics'
import type {Question} from '../../types/Question'

export const getStaticPaths: GetStaticPaths = async () => {
  const questions = await getQuestions()
  const paths = questions.map(({code}) => {
    return {
      params: {
        questionCode: code,
      },
    }
  })

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({params}) => {
  const questions = await getQuestions()

  const labels = await getLabels('questionnaire')

  return {props: {questions, labels}}
}
interface Props {
  questions: Question[]
  labels: Record<string, string>
}

export default function QuestionnaireRoute({questions, labels}: Props): React.ReactElement {
  const router = useRouter()
  const analytics = useAnalytics()
  const {device, isMobile} = useUserDevice()
  const [answers, setAnswers] = useState({})

  const firstQuestion = questions[0]
  const getCurrentQuestion = () => {
    let {questionCode = questions[0].code} = router.query
    questionCode = Array.isArray(questionCode) ? questionCode[0] : questionCode
    return questions.find(({code}) => code == questionCode)
  }

  const saveToSession = (question: Question, selectedAnswer?: string) => {
    if (question.analyticsEventObjectKey) {
      const allAnswers = sessionStorage.get(ANALYTICS_QUESTIONNAIRE_STORAGE_KEY) || {}
      allAnswers[question.analyticsEventObjectKey] = selectedAnswer
      if (question.analyticsEventObjectKey == 'role') {
        allAnswers['role_order'] = question.options.findIndex(({key}) => key === selectedAnswer) + 1
      }

      sessionStorage.set(ANALYTICS_QUESTIONNAIRE_STORAGE_KEY, allAnswers)
    }
  }

  const onNextQuestion = (question: Question, answer?: string) => {
    const hasSkippedPreviouslyAnsweredQuestion = !answer && answers[question.code]
    const index = getQuestionIndex(question.code)
    const next = questions[index + 1]

    if (!hasSkippedPreviouslyAnsweredQuestion) {
      setAnswers({...answers, [question.code]: answer})
      saveToSession(question, answer)

      analytics.sendEvent(
        new Datalayer('onboardingQuestionAnswered', {
          app_type: isMobile ? 'mobile-web' : 'desktop',
          device_os: device,
          device_type: isMobile ? 'mobile' : 'desktop',
          page: router.route,
          question: question.code,
          question_order: question.order,
          answer,
        }),
      )
    }

    if (next) {
      // Shallow is used for making the transition on the client side
      // rather than reloading the entire page
      // ? We still generate all the pages by `next export`
      router.push(`/questionnaire/${next.code}`, undefined, {
        shallow: true,
      })
    } else {
      router.push(`/team`)
    }
  }

  const onBack = () => {
    router.back()
  }

  const currentQuestion = getCurrentQuestion()

  useEffect(() => {
    if (router?.query?.returnUrl) {
      sessionStorage.set(RETURN_URL_STORAGE_KEY, router?.query?.returnUrl)
    }
  }, [router])

  return (
    <Questionnaire
      question={currentQuestion}
      onNext={onNextQuestion}
      onBack={onBack}
      isBackVisible={currentQuestion.code !== firstQuestion.code}
      numberOfAllQuestions={questions.length}
      numberOfCurrentQuestion={getQuestionIndex(currentQuestion.code) + 1}
      labels={labels}
      activeAnswer={answers[currentQuestion.code]}
    ></Questionnaire>
  )

  function getQuestionIndex(questionCode: string) {
    return questions.findIndex(({code}) => code == questionCode)
  }
}
